import type { SncrNumeracaoFaixa, SncrNotificacaoResponse } from "../types";

function normalizeNumeros(
  value: SncrNotificacaoResponse["numeracoesReceita"],
): string[] {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value.map(String).map((item) => item.trim()).filter(Boolean);
  }

  const trimmed = value.trim();
  if (!trimmed) return [];

  if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
    try {
      const parsed = JSON.parse(trimmed.replace(/'/g, '"'));
      if (Array.isArray(parsed)) {
        return parsed.map(String).map((item) => item.trim()).filter(Boolean);
      }
    } catch {
      return trimmed
        .slice(1, -1)
        .split(",")
        .map((item) => item.trim().replace(/^["']|["']$/g, ""))
        .filter(Boolean);
    }
  }

  return [trimmed];
}

export function expandNumeracaoRange(
  inicio: string,
  fim: string,
  quantidade?: number,
): string[] {
  const startRaw = inicio.trim();
  const endRaw = fim.trim();
  const startMatch = startRaw.match(/^(.*?)(\d+)$/);
  const endMatch = endRaw.match(/^(.*?)(\d+)$/);

  if (!startMatch || !endMatch) {
    throw new Error(
      "Formato de faixa SNCR inválido (esperado sufixo numérico em inicio/fim).",
    );
  }

  const [, prefixStart, startDigits] = startMatch;
  const [, prefixEnd, endDigits] = endMatch;

  if (prefixStart !== prefixEnd) {
    throw new Error(
      "Inicio e fim da numeração SNCR não compartilham o mesmo prefixo.",
    );
  }

  const pad = Math.max(startDigits.length, endDigits.length);
  const startNum = Number(startDigits);
  const endNum = Number(endDigits);

  if (
    !Number.isFinite(startNum) ||
    !Number.isFinite(endNum) ||
    endNum < startNum
  ) {
    throw new Error("Intervalo de numeração SNCR inválido.");
  }

  const fromRange = endNum - startNum + 1;
  const count =
    typeof quantidade === "number" && quantidade > 0 ? quantidade : fromRange;

  if (count > 50_000) {
    throw new Error("Quantidade de numerações SNCR excede o limite seguro.");
  }

  const lastNum =
    typeof quantidade === "number" && quantidade > 0
      ? startNum + quantidade - 1
      : endNum;

  const numeros: string[] = [];
  for (let n = startNum; n <= lastNum; n += 1) {
    numeros.push(`${prefixStart}${String(n).padStart(pad, "0")}`);
  }

  return numeros;
}

export function buildFaixaFromResponse(
  response: SncrNotificacaoResponse,
): SncrNumeracaoFaixa {
  const fromList = normalizeNumeros(response.numeracoesReceita);
  if (fromList.length) {
    return {
      inicio: fromList[0],
      fim: fromList[fromList.length - 1],
      numeros: fromList,
      quantidade: fromList.length,
    };
  }

  const inicio = response.inicio?.trim();
  const fim = response.fim?.trim();
  if (inicio && fim) {
    const numeros = expandNumeracaoRange(inicio, fim, response.quantidade);
    if (!numeros.length) {
      throw new Error("A API SNCR não retornou numerações de receita.");
    }
    return {
      inicio: numeros[0],
      fim: numeros[numeros.length - 1],
      numeros,
      quantidade: numeros.length,
    };
  }

  throw new Error("A API SNCR não retornou numerações de receita.");
}
